import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import { Percent, Save, Loader2, AlertCircle, CheckCircle } from 'lucide-react';

const SuperAdminCommission = ({ authToken }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rows, setRows] = useState([]);
  const [edits, setEdits] = useState({});

  const fetchCommission = () => {
    setLoading(true);
    setError('');
    fetch(`${API_BASE_URL}/admin/super/commission`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load commission');
        return res.json();
      })
      .then((data) => {
        setRows(data.commission || []);
        setEdits({});
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCommission();
  }, [authToken]);

  const handlePercentChange = (id, value) => {
    // Keep as string to allow typing decimal points fluently (e.g., "15.")
    setEdits((prev) => ({ ...prev, [id]: value }));
  };

  const saveOne = (catId) => {
    const rawValue = edits[catId];
    const pct = rawValue !== undefined ? parseFloat(rawValue) : rows.find((r) => r.id === catId)?.commission_percent ?? 0;

    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
      setError('Please enter a valid percentage between 0 and 100');
      return;
    }

    setSaving(catId);
    setSuccess('');
    setError('');
    fetch(`${API_BASE_URL}/admin/super/commission/${catId}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ commission_percent: pct }),
    })
      .then((res) => (res.ok ? res.json() : res.json().then((e) => Promise.reject(new Error(e.error || 'Save failed')))))
      .then(() => {
        setSuccess('Saved');
        setEdits((prev) => {
          const next = { ...prev };
          delete next[catId];
          return next;
        });
        fetchCommission();
      })
      .catch((err) => setError(err.message))
      .finally(() => setSaving(null));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-4">Commission by Category</h2>
      <p className="text-gray-600 mb-6">Set commission % per course category (platform-wide).</p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center text-red-700">
          <AlertCircle className="h-4 w-4 mr-2" />
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-center text-green-700">
          <CheckCircle className="h-4 w-4 mr-2" />
          {success}
        </div>
      )}

      {rows.length === 0 ? (
        <p className="text-gray-500">No course categories yet. Add categories under Course Categories first.</p>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Category</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Commission %</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.map((r) => {
                const isEdited = edits[r.id] !== undefined;
                const displayValue = isEdited ? edits[r.id] : r.commission_percent;

                return (
                  <tr key={r.id} className={isEdited ? 'bg-indigo-50/30' : ''}>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900">{r.name}</td>
                    <td className="px-4 py-4">
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          className={`w-24 border rounded-md px-3 py-1.5 text-sm transition-all shadow-sm ${isEdited ? 'border-indigo-400 ring-1 ring-indigo-400 bg-white' : 'border-gray-200'
                            }`}
                          value={displayValue}
                          onChange={(e) => handlePercentChange(r.id, e.target.value)}
                        />
                        <span className="text-gray-400 text-sm">%</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <button
                        type="button"
                        onClick={() => saveOne(r.id)}
                        disabled={saving === r.id || (!isEdited && r.commission_percent !== undefined)}
                        className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-sm ${isEdited
                            ? 'bg-indigo-600 text-white hover:bg-indigo-700 active:scale-95'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                      >
                        {saving === r.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            <span>Save</span>
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SuperAdminCommission;
